/**
 * Global Streaming State Tracker
 *
 * This module tracks whether a streaming session is active.
 * Used by the PWA update hook to defer auto-updates during streaming.
 *
 * Why a module instead of React state?
 * - The PWA service worker operates outside React's lifecycle
 * - We need synchronous access to the streaming state
 * - Multiple components (baby/parent pages) may set this state
 */

// Streaming state
let isStreamingActive = false;

// Debounce timer for streaming end (prevents race conditions)
let streamingEndTimeout: ReturnType<typeof setTimeout> | null = null;

// Delay before triggering callbacks when streaming ends (ms)
// This prevents PWA update if user quickly reconnects
const STREAMING_END_DEBOUNCE_MS = 2000;

// Callbacks to notify when streaming ends (for pending updates)
const onStreamingEndCallbacks: (() => void)[] = [];

/**
 * Set streaming state to active
 * Call this when baby starts streaming or parent connects to stream
 */
export function setStreamingActive(active: boolean): void {
  console.log(`[StreamingState] Setting streaming active: ${active}`);

  if (active) {
    // Cancel any pending "streaming ended" callbacks
    if (streamingEndTimeout) {
      console.log("[StreamingState] Cancelling pending streaming end timeout");
      clearTimeout(streamingEndTimeout);
      streamingEndTimeout = null;
    }
    isStreamingActive = true;
  } else {
    // Debounce the streaming end to prevent race conditions
    // If streaming restarts within STREAMING_END_DEBOUNCE_MS, we won't trigger callbacks
    if (streamingEndTimeout) {
      // Already pending, don't create another
      return;
    }

    console.log(
      `[StreamingState] Scheduling streaming end in ${STREAMING_END_DEBOUNCE_MS}ms`,
    );

    streamingEndTimeout = setTimeout(() => {
      streamingEndTimeout = null;
      isStreamingActive = false;

      console.log(
        `[StreamingState] Streaming ended, triggering ${onStreamingEndCallbacks.length} callbacks`,
      );

      // Trigger all pending callbacks
      onStreamingEndCallbacks.forEach((cb) => {
        try {
          cb();
        } catch (e) {
          console.error("[StreamingState] Callback error:", e);
        }
      });

      // Clear callbacks after executing
      onStreamingEndCallbacks.length = 0;
    }, STREAMING_END_DEBOUNCE_MS);
  }
}

/**
 * Check if streaming is currently active
 */
export function isStreamingCurrentlyActive(): boolean {
  return isStreamingActive;
}

/**
 * Register a callback to be called when streaming ends
 * Used by PWA hook to defer updates
 */
export function onStreamingEnd(callback: () => void): void {
  if (!isStreamingActive && !streamingEndTimeout) {
    // If not streaming and no pending timeout, execute immediately
    callback();
    return;
  }

  // Queue the callback for when streaming ends
  onStreamingEndCallbacks.push(callback);
}

/**
 * Clear all pending callbacks (e.g., on page unload)
 */
export function clearPendingCallbacks(): void {
  onStreamingEndCallbacks.length = 0;
  if (streamingEndTimeout) {
    clearTimeout(streamingEndTimeout);
    streamingEndTimeout = null;
  }
}
